import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Accessible destructive-action confirmation (replaces window.confirm).
 *
 * Semantics come from Radix AlertDialog: role="alertdialog", focus trapped
 * while open, Escape cancels, backdrop clicks do NOT confirm, and focus
 * returns to the trigger on close. Initial focus lands on Cancel (the safe
 * action) via AlertDialogCancel being the first focusable in DOM order in
 * the footer — we additionally pin it with onOpenAutoFocus.
 *
 * When `requiredConfirmText` is provided the Confirm button is disabled until
 * the user types the exact text (case-sensitive). This is the correct pattern
 * for irreversible destructive actions such as deleting a tenant.
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel = "Delete",
  onConfirm,
  destructive = true,
  confirmTestId,
  requiredConfirmText,
  requiredConfirmLabel,
}: {
  trigger: React.ReactNode;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  destructive?: boolean;
  confirmTestId?: string;
  /** When set, user must type this exact string before the confirm button becomes active. */
  requiredConfirmText?: string;
  /** Label shown above the confirmation input field. */
  requiredConfirmLabel?: string;
}) {
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const [typedValue, setTypedValue] = React.useState("");

  const confirmed =
    !requiredConfirmText || typedValue === requiredConfirmText;

  // Reset typed value whenever the dialog opens/closes.
  React.useEffect(() => {
    if (!open) setTypedValue("");
  }, [open]);

  // Deterministic Escape (see note on onKeyDown below): a capture-phase
  // document listener also runs even when a third-party overlay (e.g. the
  // removed accessiBe widget) stops propagation of Escape before it reaches the
  // dialog or Radix's own document listener.
  React.useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      // Let a nested Radix overlay (Select/Popover/menu) consume Escape first.
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onEsc, true);
    return () => document.removeEventListener("keydown", onEsc, true);
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          cancelRef.current?.focus();
        }}
        // Deterministic Escape: third-party overlays (e.g. the since-removed accessiBe
        // widget) can swallow the document-level keydown Radix relies on.
        // Handling Escape on the content itself (bubbling from the focused
        // element inside the dialog) guarantees single-press dismissal.
        onKeyDown={(e) => {
          if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
          if (e.key === "Escape") {
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {requiredConfirmText && (
          <div className="mt-2 space-y-1.5">
            <Label htmlFor="confirm-text-input" className="text-sm font-medium">
              {requiredConfirmLabel ?? (
                <>
                  Type <span className="font-mono font-bold">{requiredConfirmText}</span> to confirm:
                </>
              )}
            </Label>
            <Input
              id="confirm-text-input"
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              placeholder={requiredConfirmText}
              autoComplete="off"
              data-testid="input-confirm-text"
              className="font-mono"
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef} data-testid="button-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (!confirmed) return;
              onConfirm();
              setOpen(false);
            }}
            disabled={!confirmed}
            data-testid={confirmTestId ?? "button-confirm-action"}
            className={destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50" : undefined}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
